import { I18n, Store } from '../wrappers';
import { CashRegister, Order, Shift, TaxItem } from '../models';
import { ClientIDType, CustomPaymentTypeLabel, OrderFlagType, OrderRegisterStatusType, OrderStatusType, PaymentTypeLabel } from 'app-types';
import { AdvanceFlowService, CertificateService, connectionService, EinvoiceService, OrderService, TenantService } from '../services';
import { fiscalService } from '../fiscal';
import Money from './Money';
export class OrderValidation {
    order;
    finalValidation;
    perfTracker;
    cardTerminalApproved;
    constructor(order, finalValidation, perfTracker, cardTerminalApproved = false) {
        this.order = order;
        this.finalValidation = finalValidation;
        this.perfTracker = perfTracker;
        this.cardTerminalApproved = cardTerminalApproved;
    }
    async validate() {
        const validations = [
            { label: 'validateAccount', validate: () => this.validateAccount() },
            { label: 'validateGeneral', validate: () => this.validateGeneral() },
            { label: 'validateUser', validate: () => this.validateUser() },
            { label: 'validateRegister', validate: () => this.validateRegister() },
            { label: 'validateOrderPayment', validate: () => this.validateOrderPayment() },
            { label: 'validateItems', validate: () => this.validateItems() },
            { label: 'validateClient', validate: () => this.validateClient() },
            { label: 'validateProforma', validate: () => this.validateProforma() },
            { label: 'validatePayments', validate: () => this.validatePayments() },
            { label: 'validateTaxes', validate: () => this.validateTaxes() },
            { label: 'validateAdvanceFinal', validate: () => this.validateAdvanceFinal() },
            { label: 'validateCorrect', validate: () => this.validateCorrect() },
            { label: 'validateExternalSettlement', validate: () => this.validateExternalSettlement() },
            { label: 'validateCertificate', validate: () => this.validateCertificate() }
        ];
        for (let i = 0; i < validations.length; i++) {
            this.perfTracker?.startStep(validations[i].label);
            let error = await validations[i].validate();
            this.perfTracker?.endStep();
            if (error) {
                if (this.finalValidation)
                    console.log(`Validation failed: ${validations[i].label} ${error}`);
                return error;
            }
        }
        return undefined;
    }
    /**
     * aria-api refuses an inactive (expired / disabled) tenant, and a Serbian till fiscalizes on the
     * device before it calls the server — a receipt issued now would be registered and never
     * reach the account. Runs first, so nothing in Order.issue() (onBeforeIssue's local changes,
     * fiscal registration, the sync) starts.
     *
     * Except a document for money a card terminal already moved: a local-session sale or void
     * (Payten) first reaches aria-api through its announce, so the refusal can arrive while the
     * terminal is busy. Its receipt or storno issues the way the first document after an expiry
     * does (SubscriptionEnforcement.md §7).
     */
    async validateAccount() {
        if (this.cardTerminalApproved)
            return undefined;
        const tenant = Store.get('tenant');
        if (TenantService.isInactive(tenant))
            return I18n.t(TenantService.inactiveMessageKey(tenant));
        return undefined;
    }
    async validateGeneral() {
        const curCashRegister = Store.get('cashRegister');
        const curShift = Shift.current();
        // The offline-duration lock is an overlay; a retail keyboard shortcut still reaches issue() under it.
        if (connectionService.isAppLocked())
            return I18n.t('connection.internet_offline_too_long');
        if (!curCashRegister || !curShift || (!this.order.shift_id && !this.order.shift_hash) || !this.order.cash_register_id)
            return I18n.t('general.action_not_possible');
        const isRetail = Store.get('isRetail');
        if (this.order.table_id && isRetail)
            return I18n.t('general.action_not_possible');
        if (await OrderService.isDeviceTimeBehind())
            return I18n.t('general.time_not_valid');
        return undefined;
    }
    async validateUser() {
        const user = this.order.getUser();
        if (!user)
            return I18n.t('general.action_not_possible');
        if (!user.receipts_allowed)
            return I18n.t('order.error.operator_code');
        return undefined;
    }
    async validateRegister() {
        if (!this.finalValidation || I18n.c('order.registerMethod') !== 'taxcore' || !this.order.shouldRegister())
            return undefined;
        if (!fiscalService.sdcStatus?.success) {
            await fiscalService.refreshSdcStatus();
            if (!fiscalService.sdcStatus?.success) {
                const errorMessage = !fiscalService.sdcStatus?.error ? I18n.t('sdc.status.not_initialized') : fiscalService.sdcStatus.error.message;
                console.log(errorMessage);
                return errorMessage;
            }
        }
        return undefined;
    }
    async validateOrderPayment() {
        if (this.order.correctsOrders())
            return undefined;
        const isOrderOnlyPayment = this.order.status === OrderStatusType.unpaid;
        const allowOrder = isOrderOnlyPayment || !this.order.paysForOrders().length;
        const isOrderPayment = !this.order.getPayments().length && this.order.flag?.includes(CustomPaymentTypeLabel.order);
        if (isOrderOnlyPayment && !isOrderPayment)
            return I18n.t('order.error.not_valid');
        if (isOrderPayment && !allowOrder)
            return I18n.t('order.error.not_valid');
        return undefined;
    }
    async validateItems() {
        if (!this.order.items.length)
            return I18n.t('order.error.items_missing');
        const hasItemsWithEmptyName = this.order.items.some(item => !item.name);
        const hasItemsWithZeroCount = this.order.items.some(item => item.getCount() === 0);
        const hasItemsWithZeroPrice = this.order.items.some(item => item.getUnitPrice().toUnit() === 0);
        if (hasItemsWithZeroPrice)
            return I18n.t('order.error.item_price_zero');
        if (hasItemsWithZeroCount || hasItemsWithEmptyName)
            return I18n.t('order.error.not_valid');
        const hasItemsWithNegativeCount = this.order.items.some(item => item.getCount() < 0);
        const allItemsHaveNegativeCounts = this.order.items.every(item => item.getCount() < 0);
        const hasReference = this.order.flag?.includes(OrderFlagType.ref) || this.order.correctsOthers();
        const canHaveNegativeCounts = this.order.correctsOthers()
            || this.order.isPaid()
            || this.order.isProforma()
            || this.order.isAdvance()
            || this.order.isCopy()
            || this.order.refers_to.some(o => o.reference?.type === 'proforma');
        if (hasItemsWithNegativeCount && (!allItemsHaveNegativeCounts || !canHaveNegativeCounts))
            return I18n.t('order.error.not_valid');
        if (this.order.isPaid() || this.order.isAdvance()) {
            if (hasItemsWithNegativeCount && !hasReference && !this.order.correctsOthers()) {
                return I18n.t('order.error.reference_not_present');
            }
        }
        if (this.order.isAdvance() && !I18n.c('order.allowAdvanceMultipleQuantity')) {
            const hasItemsWithInvalidQuantities = this.order.items.some(item => Math.abs(item.getCount()) !== 1);
            if (hasItemsWithInvalidQuantities)
                return I18n.t('order.error.item_count_advance');
        }
        return undefined;
    }
    async validateClient() {
        const client = this.order?.getClient();
        const hasInvoicePayment = !!this.order.getPayments().some(p => p.key === PaymentTypeLabel.invoice);
        const isInvoice = (this.order.isPaid() || this.order.isSummary()) && hasInvoicePayment && !this.order.isEinvoice();
        const isInvoiceRequiringClient = I18n.c('order.invoiceRequiresClient') && isInvoice;
        const isProformaFinalNotRequiringClient = I18n.c('order.registerMethod') === 'taxcore' && this.order.refers_to.some(order => order.reference?.type === 'proforma');
        const correctsOthers = !!this.order.getFlag(OrderFlagType.correctType)[0];
        const correctsError = this.order.getFlag(OrderFlagType.reason)[0] === 'error';
        const correctRequiringClient = this.order.isPaid() && correctsOthers && !correctsError && I18n.c('order.refundRequiresClient');
        const clientRequiredButNotPresent = !client && (!isProformaFinalNotRequiringClient && (isInvoiceRequiringClient || this.order.isEinvoice() || this.order.isAdvance() || correctRequiringClient));
        const clientNotValid = client && !client.isValid();
        if (clientRequiredButNotPresent || clientNotValid)
            return I18n.t('invoice.warning.buyer_general');
        // An identified buyer must resolve to an identification the fiscal system can
        // carry: getFullIdNumber() is market-localized and comes back empty when the
        // id type is not one the market knows (TaxCore wants "<type>:<number>") —
        // the invoice would otherwise leave with the buyer silently dropped. A storno
        // issued for an operator error never carries the buyer (the request stamps
        // the tenant's own id instead), so it is exempt.
        if (client?.id_number && !client.getFullIdNumber() && !(correctsOthers && correctsError))
            return I18n.t('invoice.warning.buyer_id_type');
        if (this.order.isEinvoice()) {
            // Check EInvoice Client Valid
            if (!client?.einvoiceAllowed())
                return I18n.t('invoice.warning.buyer_einvoice');
            // The e-invoice register request is built by the e-invoice flow (the Details form)
            // after this gate can already run — its absence means the order is not ready to
            // issue, which is a validation outcome, not a crash.
            const registerRequest = this.order.register_request;
            if (!registerRequest)
                return I18n.t('order.error.not_valid');
            // Check EInvoice VAT Obligation Valid
            if (registerRequest.vat_obligation === 'by_due_date' && !registerRequest.due_date) {
                return !this.order.isAdvance() ? I18n.t('einvoice.error.due_date_required') : I18n.t('einvoice.error.paid_date_advance_required');
            }
            // Check EInvoice JBKJS
            if (client?.id_type === ClientIDType.tax_id_number_local_jbkjs) {
                if (!registerRequest?.contract_number && !registerRequest?.invoice_number && !registerRequest?.agreement_number) {
                    return I18n.t('order.error.not_valid');
                }
            }
        }
        if (this.order.export && client?.isLocalCountry())
            return I18n.t('invoice.error.buyer_country_export');
        return undefined;
    }
    async validateProforma() {
        if (!this.order.isProforma())
            return undefined;
        // A proforma carries the number the cashier gives it ("Broj predračuna"): a Montenegrin proforma
        // has no other. Its storno carries the proforma's reference instead of a number of its own.
        if (!this.order.canceled()) {
            if (!this.order.internal_number && !this.order.correctsOthers())
                return I18n.t('order.proforma_number_enter');
            return undefined;
        }
        if (!this.order.getPayments().length)
            return I18n.t('order.payments_choose');
        if (!this.order.internal_number)
            return I18n.t('order.internal_number_enter');
        return undefined;
    }
    async validatePayments() {
        // External documents carry no POS payments — the bill is settled
        // outside the POS (a room charge and its full-cancel corrective), so
        // amount/payment matching doesn't apply. The server likewise never
        // validates payments for them.
        if (this.order.isExternal())
            return undefined;
        const isPaid = [OrderStatusType.paid, OrderStatusType.advance, OrderStatusType.summary].includes(this.order.status);
        if (this.finalValidation && isPaid && this.order.getToPay().abs().add(this.order.getChange().abs()).toRoundedUnit(4) !== this.order.getPaid().abs().toRoundedUnit(4))
            return I18n.t('order.error.not_valid');
        const paymentTypes = this.order.getPayments().map(p => p.key) || [];
        const isOrderPayment = this.order.flag?.includes(CustomPaymentTypeLabel.order);
        const isOrderCorrect = this.order.correctsOrders()[0]?.isUnpaid();
        if (this.finalValidation && !isOrderPayment && !isOrderCorrect && !paymentTypes.length)
            return I18n.t('order.payments_choose');
        if (paymentTypes.length && !this.isAutomaticAdvanceClosing() && !CashRegister.current().allowsPayment(paymentTypes)) {
            const onlyCashAndCard = !paymentTypes.some(p => p !== PaymentTypeLabel.cash && p !== PaymentTypeLabel.credit_card);
            const allowCard = I18n.c('cashRegister.allowLimitedPayments') && onlyCashAndCard;
            if (!allowCard)
                return I18n.t('order.error.payment_type_not_allowed');
        }
        return undefined;
    }
    /**
     * An advance closing the POS issues by itself, or its undo (advances used in parts): it pays back the way
     * the advance was paid — perhaps by a method this register doesn't offer (the advance came from
     * another register, or the method was switched off since). The register's payment settings govern
     * what a cashier may choose, and nobody chooses here; refused, the closing would never reach EFI.
     */
    isAutomaticAdvanceClosing() {
        return I18n.c('order.advanceInParts') && this.order.isAutomaticAdvanceClosing();
    }
    async validateTaxes() {
        if (this.finalValidation && !this.order.taxes.length)
            return I18n.t('order.error.not_valid');
        return undefined;
    }
    async validateAdvanceFinal() {
        if (!this.order.refersToAdvance())
            return undefined;
        // A final is issued paid: as an unpaid Order it would close nothing, and paying it later would charge
        // the advance's part again.
        if (this.order.status === OrderStatusType.unpaid)
            return I18n.t('order.error.advance_final_unpaid');
        const advanceRefOrders = this.order.referredAdvanceOrders();
        const mustBeEinvoice = advanceRefOrders[0].isEinvoice();
        if (!this.order.isEinvoice() && mustBeEinvoice)
            return I18n.t('order.error.advance_final_must_be_einvoice');
        if (I18n.c('order.advanceInParts'))
            return this.validateAdvanceFinalInParts();
        const availableAdvanceAmount = advanceRefOrders.reduce((acc, cur) => acc.add(cur.getRemainingAdvanceAmount().abs()), Money.parse(0));
        // final order must have the same tax items as the referred advance orders
        const advanceTaxAmount = advanceRefOrders.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0));
        const advanceRefOrderTaxRates = TaxItem.merge(advanceRefOrders.flatMap(o => o.taxes));
        const taxRateMismatch = advanceRefOrderTaxRates.some(t => {
            const orderTaxItem = this.order.taxes.find(ot => ot.id === t.id);
            if (!orderTaxItem || orderTaxItem.getValue().abs().toRoundedUnit(2) < t.getValue().abs().toRoundedUnit(2))
                return true;
            return false;
        });
        if (taxRateMismatch && advanceTaxAmount.toUnit() !== 0)
            return I18n.t('order.error.advance_tax_rates_mismatch');
        // TODO uncomment and test
        // const availableAdvanceTaxes = advanceRefOrders.flatMap(o => o.getRemainingTaxes()).map(t => {
        //     const taxItem = t.$duplicate();
        //     taxItem.price_before_tax = Money.parse(taxItem.price_before_tax).multiply(-1).toFormat('0.0000');
        //     taxItem.value = Money.parse(taxItem.value).multiply(-1).toFormat('0.0000');
        //     return taxItem;
        // });
        // const resultingTaxes = TaxItem.merge([...order.taxes, ...availableAdvanceTaxes])
        // if final receipt amount is less than available advance amount
        if (availableAdvanceAmount.toUnit() > this.order.getPaidAdvanceEffective().toUnit()) {
            // must only have one large advance to refer to and that advance must not be refund
            if (advanceRefOrders.length > 1 || advanceRefOrders[0].getToPay().toUnit() < 0) {
                // TODO check tax items
                return I18n.t('order.error.advance_smaller_than_expected');
            }
            // TODO uncomment and test
            // const taxesMoreThanAdvance = resultingTaxes.some(t => {
            //     return parseFloat(t.value) >= 0 || parseFloat(t.price_before_tax) >= 0;
            // })
            // if(taxesMoreThanAdvance) {
            //     return { success: false, error: I18n.t('order.error.advance_taxes_smaller_than_expected') };
            // }
            if (advanceRefOrders[0].isMixedPayment())
                return I18n.t('order.error.advance_mixed_payment');
            const uniqueTaxRates = this.order.items.map(i => i.tax_rate).filter((rate, index, array) => array.indexOf(rate) == index);
            const advanceUniqueTaxRates = advanceRefOrders[0].items.map(i => i.tax_rate).filter((rate, index, array) => array.indexOf(rate) == index);
            if (uniqueTaxRates.length !== 1 || advanceUniqueTaxRates.length !== 1)
                return I18n.t('order.error.advance_single_tax_rate');
            if (uniqueTaxRates[0] != advanceUniqueTaxRates[0]) {
                return I18n.t('order.error.advance_tax_rate_mismatch', {
                    value1: parseFloat(uniqueTaxRates[0]) + '%',
                    value2: parseFloat(advanceUniqueTaxRates[0]) + '%'
                });
            }
        }
        // if final receipt amount is equal or greater than available advance amount
        // TODO uncomment and test
        // else {
        // const taxesLessThanAdvance = resultingTaxes.some(t => {
        //     return parseFloat(t.value) < 0 || parseFloat(t.price_before_tax) < 0;
        // })
        // if(taxesLessThanAdvance) {
        //     return { success: false, error: I18n.t('order.error.advance_taxes_larger_than_expected') };
        // }
        // }
        return undefined;
    }
    /**
     * Advances used in parts (`order.advanceInParts`: Montenegro, Bosnia): a final pays something from its
     * advance, never more than this register sees is left of it, and an advance paid by mixed methods only
     * when the final uses all of it (AdvanceFlowService).
     */
    validateAdvanceFinalInParts() {
        const advance = this.order.storedAdvance();
        const paid = this.order.getPaidAdvanceEffective();
        // A cart that lost the advance's credit (rebuilt without its flags) would name the advance and pay
        // nothing from it; a cart made before the advance was used again would pay more than is left.
        if (paid.toUnit() <= 0 || paid.toUnit() > AdvanceFlowService.left(advance, this.order).toUnit())
            return I18n.t('order.error.advance_changed');
        // The closing pays back the way the advance was paid, and only a closing in full pays back more
        // than one method.
        if (advance.isMixedPayment() && !AdvanceFlowService.closesInFull(advance, paid))
            return I18n.t('order.error.advance_mixed_payment_in_full');
        return undefined;
    }
    async validateCorrect() {
        const correctType = this.order.getFlag(OrderFlagType.correctType)[0];
        if (correctType === 'correct' || correctType === 'correctSummary') {
            if (!this.order.isPaid() || this.order.isMixedPayment())
                return I18n.t('order.error.not_valid');
        }
        if (correctType === 'cancel' || correctType === 'cancelSummary') {
            if (this.order.isProforma() && !this.order.isFiscal())
                return I18n.t('order.error.not_valid');
        }
        return undefined;
    }
    /**
     * Reject paying, summarizing or crediting an externally settled order (e.g.
     * a hotel room charge): the charge lives in the external system, so
     * fiscalizing over it would charge/credit the guest twice. Only
     * VALUE-TRANSFERRING references are blocked — paying via 'summary'
     * references and crediting via credit-note item refs. Full-cancel
     * correctives are exempt (cancelling a settled order IS the storno flow)
     * and document flows (copy, proforma, advance, einvoice refs) stay allowed.
     * Runs on every issue, so it also covers a terminal whose UI guards passed
     * on stale data. The server enforces the same rule.
     */
    async validateExternalSettlement() {
        // Prefer the live ORM record over the reference snapshot — a settle
        // broadcast may have landed after this order was prepared.
        const correctiveRef = this.order.refers_to.find(ref => ref.reference?.type === 'corrective');
        if (correctiveRef) {
            // An externally settled order may only be cancelled in full: the storno
            // reverses the whole charge, so a partial correction would leave the
            // external ledger and the receipt out of step, and reversing a refund
            // would re-storno.
            const corrected = Order.find(correctiveRef.hash) ?? correctiveRef;
            const isFullCancel = ['cancel', 'cancelSummary'].includes(this.order.getFlag(OrderFlagType.correctType)[0]);
            if (corrected.isSettledExternally() && (!isFullCancel || corrected.correctsOthers())) {
                return I18n.t('order.error.settled_externally_full_cancel_only');
            }
            return undefined;
        }
        const referenced = this.order.refers_to
            .filter(ref => ref.reference?.type === 'summary')
            .map(ref => Order.find(ref.hash) ?? ref);
        if (this.order.isCreditNote()) {
            referenced.push(...this.order.items
                .map(item => item.ref_order_hash)
                .filter((hash) => !!hash)
                .map(hash => Order.find(hash))
                .filter((order) => !!order));
        }
        if (referenced.some(order => order?.isSettledExternally())) {
            return I18n.t('order.error.settled_externally');
        }
        return undefined;
    }
    async validateCertificate() {
        if (!this.finalValidation)
            return undefined;
        const certInfo = await CertificateService.getInfo();
        if (certInfo.isExpired)
            return certInfo.warningMsg;
        return undefined;
    }
    async validateEinvoice(action) {
        if (!connectionService.isOnline)
            return I18n.t('connection.internet_offline_message_not_allowed');
        if (!EinvoiceService.isEnabled())
            return I18n.t('einvoice.error.not_enabled');
        const approvedActions = this.order.isIncoming() ? ['approve', 'reject'] : ['storno'];
        if (!approvedActions.includes(action))
            return I18n.t('order.error.not_valid');
        let approvedStatuses = [];
        if (action == 'reject') {
            approvedStatuses = [OrderRegisterStatusType.einvoiceNew, OrderRegisterStatusType.einvoiceSeen];
        }
        else if (action == 'approve') {
            approvedStatuses = [OrderRegisterStatusType.einvoiceNew, OrderRegisterStatusType.einvoiceSeen, OrderRegisterStatusType.einvoiceRejected];
        }
        else if (action == 'storno') {
            approvedStatuses = [OrderRegisterStatusType.einvoiceSent, OrderRegisterStatusType.einvoiceApproved];
        }
        if (!this.order.register_status || !approvedStatuses.includes(this.order.register_status))
            return I18n.t('order.error.not_valid');
        const advanceRefOrders = this.order.referredAdvanceOrders();
        const availableAdvanceAmount = advanceRefOrders.reduce((acc, cur) => acc.add(cur.getRemainingAdvanceAmount().abs()), Money.parse(0));
        // if final receipt amount is less than available advance amount
        // TODO remove - temporarily disabled larger advance for e-invoices
        if (availableAdvanceAmount.toUnit() > this.order.getPaidAdvanceEffective().toUnit()) {
            return I18n.t('order.error.advance_larger_than_expected');
        }
        return undefined;
    }
}
export default OrderValidation;
//# sourceMappingURL=OrderValidation.js.map